import { Box, Center, Flex, HStack, Progress, Spinner, VStack } from "@chakra-ui/react";
import { useEffect } from "react";
import { Link } from "react-router";
import CDS from "design-system";
import { Card } from "design-system/components/Card";
import { Heading22 } from "components/Heading";
import { InternalLink } from "components/Link";
import AppPage from "layouts/AppPage";
import { useNavbar } from "hooks/useNavbar";
import { useAnalytics } from "services/analytics";
import {
  NEW_HIRE_ROUTES,
  PEOPLE_TEAM_ROUTES,
  SHARED_ROUTES,
} from "definitions/constants/routeConstants";
import { useDashboardHomeQuery } from "./hooks";
import { DashboardHomePayload, DashboardJourneyPreview } from "./types";

export default function DashboardPage() {
  const { setBackLocationPath, setPageTitle } = useNavbar();
  const { trackPageView } = useAnalytics();
  const { data, isLoading } = useDashboardHomeQuery();

  useEffect(() => {
    setBackLocationPath(null);
    setPageTitle(null);
    return () => {
      setBackLocationPath(null);
      setPageTitle(null);
    };
  }, [setBackLocationPath, setPageTitle]);

  useEffect(() => {
    trackPageView("dashboard", { dashboard_type: "unified" });
  }, [trackPageView]);

  if (isLoading || !data) {
    return (
      <AppPage>
        <Center py={20}>
          <Spinner size="lg" />
        </Center>
      </AppPage>
    );
  }

  return (
    <AppPage>
      <VStack align="stretch" spacing={6} pb={8}>
        <WelcomeHeader user={data.user} />

        <Flex
          gap={4}
          direction={{ base: "column", md: "row" }}
          align="stretch"
          wrap={{ base: "nowrap", md: "wrap", lg: "nowrap" }}
        >
          {data.myOnboarding && <MyOnboardingCard onboarding={data.myOnboarding} />}
          <MyTasksCard counts={data.myTasks} />
          {data.myJourneys.total > 0 && <MyJourneysCard journeys={data.myJourneys} />}
          {data.peopleTeamHub && <PeopleTeamHubCard hub={data.peopleTeamHub} />}
        </Flex>

        {data.myJourneys.list.length > 0 && <ActiveJourneysList list={data.myJourneys.list} />}
      </VStack>
    </AppPage>
  );
}

function WelcomeHeader({ user }: { user: DashboardHomePayload["user"] }) {
  return (
    <Box>
      <Heading22>{`Hi${user.firstName ? `, ${user.firstName}` : "!"} 🌱`}</Heading22>
    </Box>
  );
}

interface DashboardLinkCardProps {
  to: string;
  label: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  accent?: "default" | "highlight" | "onboarding";
  testid?: string;
}

const ACCENT_STYLES = {
  default: { bg: "gray.50" },
  highlight: { bg: "#eeedfb" },
  onboarding: { bg: "#e8f5ed" },
};

function DashboardLinkCard({
  to,
  label,
  primary,
  secondary,
  accent = "default",
  testid,
}: DashboardLinkCardProps) {
  const styles = ACCENT_STYLES[accent];
  return (
    <Link
      to={to}
      style={{ textDecoration: "none", flex: 1, minWidth: 0 }}
      data-testid={testid}
    >
      <Card
        elevation="none"
        border={false}
        radius="medium"
        padding="medium"
        backgroundColor={styles.bg}
        height="100%"
        _hover={{
          cursor: "pointer",
          shadow: "md",
        }}
        transition="box-shadow 0.15s ease"
      >
        <VStack align="stretch" spacing={2} minH="100px" height="100%">
          <CDS.Text10
            letterSpacing="0.06em"
            color="gray.500"
            textTransform="uppercase"
            noOfLines={1}
          >
            {label}
          </CDS.Text10>
          <Box flex={1}>{primary}</Box>
          {secondary && (
            <Box fontSize="xs" color="gray.600" noOfLines={1}>
              {secondary}
            </Box>
          )}
        </VStack>
      </Card>
    </Link>
  );
}

function MyOnboardingCard({
  onboarding,
}: {
  onboarding: NonNullable<DashboardHomePayload["myOnboarding"]>;
}) {
  return (
    <DashboardLinkCard
      to={NEW_HIRE_ROUTES.dashboard}
      label="My onboarding 🌱"
      accent="onboarding"
      primary={
        <VStack align="stretch" spacing={2}>
          <CDS.Text14 fontWeight="600" noOfLines={1}>
            Day {onboarding.dayNumber} · {onboarding.stageLabel}
          </CDS.Text14>
          <Progress
            value={onboarding.progressPercent}
            size="xs"
            colorScheme="green"
            borderRadius="full"
            bg="white"
          />
        </VStack>
      }
      secondary={onboarding.nextTask ? `Next: ${onboarding.nextTask.title}` : "All caught up"}
    />
  );
}

function MyTasksCard({ counts }: { counts: DashboardHomePayload["myTasks"] }) {
  return (
    <DashboardLinkCard
      to={SHARED_ROUTES.myTasks}
      label="My tasks"
      primary={
        <CDS.Text18 fontSize="2xl" fontWeight="600" lineHeight={1.1}>
          {counts.open}
        </CDS.Text18>
      }
      secondary={
        counts.overdue > 0 ? (
          <CDS.Text10 as="span" color="red.600" fontWeight={500}>
            {counts.overdue} overdue
          </CDS.Text10>
        ) : (
          "No overdue tasks"
        )
      }
    />
  );
}

function MyJourneysCard({ journeys }: { journeys: DashboardHomePayload["myJourneys"] }) {
  return (
    <DashboardLinkCard
      to={SHARED_ROUTES.myJourneys}
      label="Onboardings I'm in"
      primary={
        <CDS.Text18 fontSize="2xl" fontWeight="600" lineHeight={1.1}>
          {journeys.total}
        </CDS.Text18>
      }
      secondary={
        journeys.needsAttention > 0 ? (
          <CDS.Text10 as="span" color="red.600" fontWeight={500}>
            {journeys.needsAttention} need{journeys.needsAttention === 1 ? "s" : ""} attention
          </CDS.Text10>
        ) : (
          "All on track"
        )
      }
    />
  );
}

function PeopleTeamHubCard({ hub }: { hub: NonNullable<DashboardHomePayload["peopleTeamHub"]> }) {
  return (
    <DashboardLinkCard
      to={PEOPLE_TEAM_ROUTES.dashboard}
      label="People team hub"
      accent="highlight"
      primary={
        <CDS.Text14 fontWeight="600" noOfLines={1}>
          Company-wide
        </CDS.Text14>
      }
      secondary={`${hub.activeCount} active · ${hub.issuesCount} issue${hub.issuesCount === 1 ? "" : "s"}`}
    />
  );
}

function ActiveJourneysList({ list }: { list: DashboardJourneyPreview[] }) {
  return (
    <Box>
      <Box mb={2}>
        <CDS.Text14 fontWeight="600">
          Active journeys
        </CDS.Text14>
      </Box>
      <VStack align="stretch" spacing={0} divider={<Box borderTop="1px solid" borderColor="gray.100" />}>
        {list.map((entry) => (
          <ActiveJourneyRow key={entry.id} entry={entry} />
        ))}
      </VStack>
    </Box>
  );
}

function ActiveJourneyRow({ entry }: { entry: DashboardJourneyPreview }) {
  const needsAttention = entry.attention === "needs_attention";
  const subParts = [
    entry.roleLabel,
    `Day ${entry.dayNumber}`,
    entry.overdueCount > 0 ? `${entry.overdueCount} overdue` : null,
    entry.issueCount > 0 ? `${entry.issueCount} issue${entry.issueCount === 1 ? "" : "s"}` : null,
  ].filter(Boolean);

  return (
    <HStack py={3} spacing={3} align="center">
      <Box flex={1} minW={0}>
        <InternalLink to={`/new-hires/${entry.id}`}>
          <CDS.Text12 fontWeight={500}>
            {entry.newHireName}
          </CDS.Text12>
        </InternalLink>
        <CDS.Text10 color="gray.500" mt={0.5}>
          {subParts.join(" · ")}
        </CDS.Text10>
      </Box>
      <Box
        as="span"
        fontSize="xs"
        px={2}
        py={1}
        borderRadius="md"
        bg={needsAttention ? "red.50" : "green.50"}
        color={needsAttention ? "red.700" : "green.700"}
        whiteSpace="nowrap"
      >
        {needsAttention ? "Needs attention" : "On track"}
      </Box>
    </HStack>
  );
}
